import React from 'react';
import moment from 'moment';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import NoDataIcon from '../../../Assets/ill-no-data.svg';
import FeeOneTimePlan from './FeeOneTimePlan';
import FeeInstallmentPlan from './FeeInstallmentPlan';
import { formatFeeDate, handleDownloadInvoice } from './feeUtils';

const FeesTab = ({
    studentFeeData,
    studentFeeLoading,
    selectedFeeCourseIndex,
    setSelectedFeeCourseIndex,
    enrolledCourses = [],
    setCancelRequestModal,
}) => {
    if (studentFeeLoading) {
        return null;
    }

    const rawFeeList = (() => {
        if (!studentFeeData) return [];
        const source = studentFeeData?.data ?? studentFeeData;
        if (!source || typeof source !== 'object') return [];

        if (Array.isArray(source)) {
            return source;
        }

        // Single legacy fee object
        if (source.totalPayable !== undefined && source.courseDetails !== undefined && !source.layout) {
            return [source];
        }

        // Object keyed by resourceType:resourceId, e.g. { "course:101": {...}, "testSeries:55": {...} }
        return Object.entries(source)
            .filter(([key, val]) => val && typeof val === 'object')
            .map(([key, val]) => {
                const [keyType, keyId] = key.includes(':') ? key.split(':') : [null, null];
                return {
                    ...val,
                    resourceType: val.resourceType || keyType || "course",
                    resourceId: val.resourceId || keyId,
                };
            });
    })();

    const feeList = rawFeeList.filter(item => item && (
        item.resourceId || item.id || item.courseName || item.courseDetails || item.totalFee !== undefined || item.totalPayable !== undefined
    ));

    if (!feeList.length) {
        return (
            <div style={{
                flex: 1,
                minHeight: 0,
                backgroundColor: "#FFFFFF",
                borderRadius: fluid(24),
                padding: `${fluid(60)} ${fluid(24)}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: fluid(12),
                boxSizing: "border-box",
            }}>
                <img src={NoDataIcon} alt="No fee data" style={{ width: fluid(220), height: fluid(220) }} />
                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(24), fontWeight: 600, color: "#191919" }}>
                    No fee details found
                </span>
                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#666666" }}>
                    There are no fee records or installment plans assigned to this student yet.
                </span>
            </div>
        );
    }

    const activeFeeIndex = Math.min(selectedFeeCourseIndex, Math.max(0, feeList.length - 1));
    const currentFee = feeList[activeFeeIndex] || {};

    let parsedCourse = null;
    if (typeof currentFee.courseDetails === 'string') {
        try {
            parsedCourse = JSON.parse(currentFee.courseDetails);
        } catch (e) {
            parsedCourse = { courseName: currentFee.courseDetails };
        }
    } else if (typeof currentFee.courseDetails === 'object' && currentFee.courseDetails !== null) {
        parsedCourse = currentFee.courseDetails;
    }

    const courseName = currentFee.courseName || parsedCourse?.courseName || "Course fee";
    const resourceId = currentFee.resourceId || currentFee.id || parsedCourse?.courseId;
    const resourceType = String(currentFee.resourceType || "course").trim();
    const isOneTime = currentFee.layout === "one-time" || ["testseries", "videoresource", "studymaterial"].includes(resourceType.toLowerCase());

    const matchedEnrolledCourse = enrolledCourses.find(c => (c.courseId && String(c.courseId) === String(resourceId)) || (c.id && String(c.id) === String(resourceId)));

    const getBatchesList = () => {
        const candidates = [
            currentFee.batches,
            currentFee.standards,
            currentFee.batch,
            parsedCourse?.batches,
            parsedCourse?.standards,
            matchedEnrolledCourse?.batches,
            matchedEnrolledCourse?.standards,
            studentFeeData?.batches,
            studentFeeData?.data?.batches,
        ];
        for (const cand of candidates) {
            if (!cand) continue;
            let parsed = cand;
            if (typeof parsed === 'string') {
                try {
                    parsed = JSON.parse(parsed);
                } catch (e) {
                    parsed = [parsed];
                }
            }
            if (!Array.isArray(parsed) && typeof parsed === 'object' && parsed !== null) {
                parsed = [parsed];
            }
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed;
            }
        }
        return [];
    };

    const formatBatchItem = (b) => {
        if (!b) return "";
        if (typeof b === 'string') return b;
        const std = (b.std !== undefined && b.std !== null && String(b.std).trim() !== "")
            ? String(b.std).trim()
            : ((b.batchName !== undefined && b.batchName !== null && String(b.batchName).trim() !== "")
                ? String(b.batchName).trim()
                : ((b.batch !== undefined && b.batch !== null && typeof b.batch === 'string' && b.batch.trim() !== "")
                    ? b.batch.trim()
                    : (b.standard || b.name || "")));
        const section = (b.section !== undefined && b.section !== null && String(b.section).trim() !== "")
            ? String(b.section).trim()
            : "";
        if (std && section) return `${std} - ${section}`;
        if (std) return std;
        if (section) return section;
        return b.name || "";
    };

    const rawBatches = getBatchesList();
    const formattedBatchInfo = rawBatches.map(formatBatchItem).filter(Boolean).join(", ");
    const batchInfo = formattedBatchInfo || (currentFee.batchInfo && !currentFee.batchInfo.toLowerCase().includes("course id") ? currentFee.batchInfo : "") || "No batch assigned";

    let parsedInstallments = [];
    if (Array.isArray(currentFee.installments)) {
        parsedInstallments = currentFee.installments;
    } else if (typeof currentFee.installmentDetails === 'string') {
        try {
            parsedInstallments = JSON.parse(currentFee.installmentDetails);
        } catch (e) {
            parsedInstallments = [];
        }
    } else if (Array.isArray(currentFee.installmentDetails)) {
        parsedInstallments = currentFee.installmentDetails;
    }

    let parsedScholarship = null;
    if (typeof currentFee.scholarshipDetails === 'string') {
        try {
            parsedScholarship = JSON.parse(currentFee.scholarshipDetails);
        } catch (e) {
            parsedScholarship = { name: currentFee.scholarshipDetails };
        }
    } else if (typeof currentFee.scholarshipDetails === 'object' && currentFee.scholarshipDetails !== null) {
        parsedScholarship = currentFee.scholarshipDetails;
    }

    const totalFee = Number(currentFee.totalFee ?? currentFee.totalPayable ?? 0);
    const totalScholarship = currentFee.totalScholarship !== null && currentFee.totalScholarship !== undefined ? Number(currentFee.totalScholarship) : null;

    const discountsList = Array.isArray(currentFee.discounts) && currentFee.discounts.length > 0
        ? currentFee.discounts
        : (totalScholarship || parsedScholarship)
            ? [{ name: parsedScholarship?.name || "Early bird", amount: totalScholarship || parsedScholarship?.amount || 0 }]
            : [];

    const handlePrev = () => {
        if (activeFeeIndex > 0) setSelectedFeeCourseIndex(prev => Math.max(0, prev - 1));
    };

    const handleNext = () => {
        if (activeFeeIndex < feeList.length - 1) setSelectedFeeCourseIndex(prev => Math.min(feeList.length - 1, prev + 1));
    };

    if (isOneTime) {
        return (
            <FeeOneTimePlan
                currentFee={currentFee}
                courseName={courseName}
                batchInfo={batchInfo}
                activeFeeIndex={activeFeeIndex}
                feeListLength={feeList.length}
                resourceType={resourceType}
                totalFee={totalFee}
                discountsList={discountsList}
                onPrevFee={handlePrev}
                onNextFee={handleNext}
                onDownloadInvoice={handleDownloadInvoice}
            />
        );
    }

    const totalPaid = Number(currentFee.totalPaid ?? currentFee.paidAmount ?? (() => {
        const paidItems = parsedInstallments.filter(i => (i.status || "").toLowerCase() === "paid" || (i.status || "").toLowerCase() === "settled");
        const paidSum = paidItems.reduce((sum, i) => sum + Number(i.amount || 0), 0);
        return paidSum > 0 ? paidSum : (currentFee.transactionIds ? totalFee : 0);
    })());

    const totalPending = Number(currentFee.totalPending ?? Math.max(0, totalFee - totalPaid));
    const percentPaid = totalFee > 0 ? Math.min(100, Math.round((totalPaid / totalFee) * 100)) : (totalPaid > 0 ? 100 : 0);
    const overdueCount = Number(currentFee.overdueCount ?? parsedInstallments.filter(i => (i.status || "").toLowerCase() === "overdue" || (i.dueDate && moment(i.dueDate).isBefore(moment(), 'day') && (i.status || "").toLowerCase() !== "paid")).length);

    const nextDue = currentFee.nextDue || (() => {
        const unpaid = parsedInstallments.filter(i => (i.status || "").toLowerCase() !== "paid");
        return unpaid.find(i => i.dueDate) || unpaid[0] || null;
    })();

    const nextDueLabel = nextDue?.dueDate
        ? formatFeeDate(nextDue.dueDate)
        : (nextDue?.name || (totalPending === 0 ? "Completed" : "N/A"));

    const defaultSplitColors = ["#1982C4", "#8AC926", "#FF595E", "#FFCA3A", "#D864E3"];

    const paymentModeSplit = (Array.isArray(currentFee.paymentModeSplit) && currentFee.paymentModeSplit.length > 0)
        ? currentFee.paymentModeSplit.map((item, idx) => ({
            ...item,
            percent: Number(item.percent ?? 0),
            color: item.color || defaultSplitColors[idx % defaultSplitColors.length]
        }))
        : [
            { mode: "UPI", percent: 0, color: "#1982C4" },
            { mode: "Card", percent: 0, color: "#8AC926" },
            { mode: "Net banking", percent: 0, color: "#FF595E" },
        ];

    const splitTotal = paymentModeSplit.reduce((s, item) => s + (item.percent || 0), 0);
    const chartSegments = splitTotal > 0
        ? paymentModeSplit.map((item, idx) => ({
            value: item.percent,
            color: item.color || defaultSplitColors[idx % defaultSplitColors.length],
        }))
        : [{ value: 100, color: "#E5E7EB" }];

    const getInstallmentNum = (inst, defaultNum) => {
        if (inst?.installmentNumber !== undefined && !isNaN(Number(inst.installmentNumber))) {
            return Number(inst.installmentNumber);
        }
        const match = String(inst?.name || '').match(/\b(\d+)(?:st|nd|rd|th)?\b/i);
        if (match) {
            return Number(match[1]);
        }
        return defaultNum;
    };

    const sortedInstallments = [...parsedInstallments].sort((a, b) => {
        const numA = getInstallmentNum(a, 0);
        const numB = getInstallmentNum(b, 0);
        if (numA !== numB) {
            return numB - numA;
        }
        const dateA = a.dueDate || a.paymentDate || '';
        const dateB = b.dueDate || b.paymentDate || '';
        if (dateA && dateB) {
            return moment(dateB).diff(moment(dateA));
        }
        return 0;
    });

    return (
        <FeeInstallmentPlan
            currentFee={currentFee}
            courseName={courseName}
            batchInfo={batchInfo}
            totalFee={totalFee}
            totalPaid={totalPaid}
            totalPending={totalPending}
            nextDueLabel={nextDueLabel}
            percentPaid={percentPaid}
            overdueCount={overdueCount}
            sortedInstallments={sortedInstallments}
            activeFeeIndex={activeFeeIndex}
            feeListLength={feeList.length}
            onPrevFee={handlePrev}
            onNextFee={handleNext}
            paymentModeSplit={paymentModeSplit}
            chartSegments={chartSegments}
            splitTotal={splitTotal}
            defaultSplitColors={defaultSplitColors}
            discountsList={discountsList}
            onDownloadInvoice={handleDownloadInvoice}
            onCancelRequestClick={(inst) => setCancelRequestModal(inst)}
        />
    );
};

export default FeesTab;
